import QuickTooltip from "#components/layout/misc/QuickTooltip";
import Divider from "#components/util/Divider";
import Muted from "#components/util/Muted";
import useLoggedInUserGameSettings from "#components/util/useLoggedInUserGameSettings";
import { ChangeOpacity } from "#util/color-opacity";
import { FormatTables } from "#util/misc";
import {
	BMS_TABLES,
	type ChartDocument,
	COLOUR_SET,
	type UserGameSettingsDocument,
} from "tachi-common";

import { DIFFICULTY_CELL_WIDTH_PX } from "./difficulty-cell-layout";
import RatingSystemPart from "./RatingSystemPart";

type BMSGames = "bms-7k" | "bms-14k" | "pms-controller" | "pms-keyboard";

const wrapLineCls = "d-block text-break";

export default function BMSOrPMSDifficultyCell({
	chart,
	game,
}: {
	chart: ChartDocument<BMSGames>;
	game: BMSGames;
}) {
	const { settings } = useLoggedInUserGameSettings() as {
		settings: UserGameSettingsDocument<"bms-7k" | "bms-14k"> | null;
	};

	const hasLevel = Object.keys((chart as ChartDocument<"bms-7k">).data.tableFolders).length > 0;

	const aiLevel =
		(game === "bms-7k" || game === "bms-14k") &&
		(chart as ChartDocument<"bms-7k" | "bms-14k">).data.aiLevel;

	let levelText = "No Rating";
	let backgroundColour = hasLevel ? COLOUR_SET.red : COLOUR_SET.gray;

	let filteredTables: Record<string, string> = {};

	if (hasLevel) {
		let tables = (chart as ChartDocument<"bms-7k">).data.tableFolders;

		const gameSpecific = settings?.preferences.gameSpecific as any;

		if (gameSpecific?.displayTables) {
			filteredTables = Object.entries(tables)
				.filter(([table, _]) => !gameSpecific.displayTables.includes(table))
				.reduce(
					(acc, [table, level]) => {
						acc[table] = level as string;
						return acc;
					},
					{} as Record<string, string>,
				);
			tables = Object.entries(tables)
				.filter(([table, _]) => gameSpecific.displayTables.includes(table))
				.reduce(
					(acc, [table, level]) => {
						acc[table] = level as string;
						return acc;
					},
					{} as Record<string, string>,
				);
		}

		levelText = FormatTables(tables as Record<string, string>);

		if (game === "bms-7k" || game === "bms-14k") {
			// use bms table colour instead of just red/gray.
			// todo: add pms table colours maybe?
			backgroundColour = FindTableColour(tables as Record<string, string>);
		}
	}

	return (
		<td
			style={{
				backgroundColor: ChangeOpacity(backgroundColour, 0.2),
				boxSizing: "border-box",
				maxWidth: `${DIFFICULTY_CELL_WIDTH_PX}px`,
				minWidth: 0,
				whiteSpace: "normal",
				width: `${DIFFICULTY_CELL_WIDTH_PX}px`,
			}}
			title={levelText}
		>
			{!hasLevel && aiLevel ? (
				<>
					<span className={`${wrapLineCls} pb-1`} style={{ minWidth: 0 }}>
						No Rating
					</span>
					<QuickTooltip tooltipContent="This rating has been generated by AI. Don't put too much trust into it.">
						<div className={wrapLineCls} style={{ minWidth: 0 }}>
							<Muted>AI{aiLevel}</Muted>
						</div>
					</QuickTooltip>
				</>
			) : Object.keys(filteredTables).length > 0 ? (
				<QuickTooltip
					tooltipContent={
						<div>
							This chart also appears in these tables, but you have them set to not
							display.
							<Divider />
							{FormatTables(filteredTables)}
						</div>
					}
				>
					<div
						className={wrapLineCls}
						style={{ minWidth: 0, textDecoration: "underline dotted" }}
					>
						{levelText || "Rating Hidden"}
					</div>
				</QuickTooltip>
			) : (
				<span className={wrapLineCls} style={{ minWidth: 0 }}>
					{levelText}
				</span>
			)}

			<RatingSystemPart chart={chart} game={game} />
		</td>
	);
}

function FindTableColour(tableFolders: Record<string, string>) {
	const lookup = new Map(BMS_TABLES.map((e) => [e.prefix, e.colour]));

	for (const table of Object.keys(tableFolders)) {
		const colour = lookup.get(table);

		if (colour) {
			return colour;
		}
	}

	return COLOUR_SET.gray;
}
